import { inject, Injectable } from '@angular/core';
import { FIRESTORE, ORGANIZATION_ID } from '@context/frontend/common';
import {
	ORGANIZATION_CONFIG_AUTHENTICATION_DOC_ID,
	ORGANIZATION_CONFIG_CHEMICALS_DOC_ID,
	ORGANIZATION_CONFIG_COLLECTION_ID,
	ORGANIZATION_CONFIG_USERS_DOC_ID,
	OrganizationConfigAuthentication,
	OrganizationConfigChemicals,
	OrganizationConfigUsers,
} from '@context/shared/types/organization';
import { doc, getDoc, setDoc } from 'firebase/firestore';

/** Default days usage logs are considered alive when no chemicals config exists. */
export const DEFAULT_USAGE_LOG_ALIVE_DAYS = 60;

export {
	DEFAULT_GUEST_AUTH_TTL_DAYS,
	DEFAULT_INVITE_EXPIRATION_DAYS,
	MAX_GUEST_AUTH_TTL_DAYS,
	MAX_INVITE_EXPIRATION_DAYS,
	MIN_GUEST_AUTH_TTL_DAYS,
	MIN_INVITE_EXPIRATION_DAYS,
} from '@context/shared/types/organization';

@Injectable({ providedIn: 'root' })
export class OrganizationConfigService {
	private readonly firestore = inject(FIRESTORE);
	private readonly organizationId = inject(ORGANIZATION_ID);

	/**
	 * Reads a config document at /organizations/{orgId}/config/{docId}.
	 * @returns Document data or null if missing or org not set.
	 */
	async getConfigDoc<T extends object>(docId: string): Promise<T | null> {
		const orgId = this.organizationId.value;
		if (!orgId) return null;

		const ref = doc(
			this.firestore,
			'organizations',
			orgId,
			ORGANIZATION_CONFIG_COLLECTION_ID,
			docId,
		);

		const snapshot = await getDoc(ref);
		if (!snapshot.exists()) return null;

		return snapshot.data() as T;
	}

	/**
	 * Writes a config document at /organizations/{orgId}/config/{docId}.
	 * Merges with existing data. Resolves when done or rejects if org not set.
	 */
	setConfigDoc<T extends object>(docId: string, data: T): Promise<void> {
		const orgId = this.organizationId.value;
		if (!orgId) {
			throw new Error('Organization ID is not set');
		}

		const ref = doc(
			this.firestore,
			'organizations',
			orgId,
			ORGANIZATION_CONFIG_COLLECTION_ID,
			docId,
		);

		return setDoc(ref, data, { merge: true });
	}

	/**
	 * Reads chemicals config at /organizations/{orgId}/config/chemicals.
	 * @returns Config or null if doc is missing. Callers should use
	 * DEFAULT_USAGE_LOG_ALIVE_DAYS when null.
	 */
	readonly getChemicalsConfig = () =>
		this.getConfigDoc<OrganizationConfigChemicals>(
			ORGANIZATION_CONFIG_CHEMICALS_DOC_ID,
		);

	/**
	 * Writes chemicals config at /organizations/{orgId}/config/chemicals.
	 * Merges with existing document.
	 */
	setChemicalsConfig(
		config: Partial<OrganizationConfigChemicals>,
	): Promise<void> {
		return this.setConfigDoc(ORGANIZATION_CONFIG_CHEMICALS_DOC_ID, config);
	}

	/**
	 * Returns the number of days usage logs are alive (from chemicals config or default).
	 */
	async getUsageLogAliveDays(): Promise<number> {
		const config = await this.getChemicalsConfig();
		return config?.usageLogAliveDays ?? DEFAULT_USAGE_LOG_ALIVE_DAYS;
	}

	/**
	 * Reads authentication config at /organizations/{orgId}/config/authentication.
	 * @returns Config or null if doc is missing. Callers should use
	 * DEFAULT_GUEST_AUTH_TTL_HOURS when null.
	 */
	readonly getAuthenticationConfig = () =>
		this.getConfigDoc<OrganizationConfigAuthentication>(
			ORGANIZATION_CONFIG_AUTHENTICATION_DOC_ID,
		);

	/**
	 * Writes authentication config at /organizations/{orgId}/config/authentication.
	 * Merges with existing document.
	 */
	setAuthenticationConfig(
		config: Partial<OrganizationConfigAuthentication>,
	): Promise<void> {
		return this.setConfigDoc(
			ORGANIZATION_CONFIG_AUTHENTICATION_DOC_ID,
			config,
		);
	}

	/**
	 * Reads users config at /organizations/{orgId}/config/users.
	 */
	readonly getUsersConfig = () =>
		this.getConfigDoc<OrganizationConfigUsers>(
			ORGANIZATION_CONFIG_USERS_DOC_ID,
		);

	/**
	 * Writes users config at /organizations/{orgId}/config/users.
	 */
	setUsersConfig(config: Partial<OrganizationConfigUsers>): Promise<void> {
		return this.setConfigDoc(ORGANIZATION_CONFIG_USERS_DOC_ID, config);
	}
}
